import type {
    AgentFilter,
    PathogenTypeEnum,
} from "@agent/domain/models/agent.model";
import { Agent } from "@agent/domain/models/agent.model";
import { AgentGetAllUseCase } from "@agent/domain/usecases/agent-get-all.usecase";
import { PathogenTypeGetAllUseCase } from "@agent/domain/usecases/pathogen-type-get-all.usecase";
import { CropTypes } from "@campaign/domain/models/crop-type.model";
import { CropTypeGetAllUseCase } from "@campaign/domain/usecases/crop-type-get-all.usecase";
import type { CheckboxOption } from "@core/domain/types/checkbox-option";
import { BaseViewModel } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class AgentPageViewmodel extends BaseViewModel {
    @observable
    cropTypes: Readonly<CropTypes> = CropTypes.empty();

    @observable
    pathogenTypes: CheckboxOption<PathogenTypeEnum>[] = [];

    @observable
    agents: Agent[] = [];

    @observable
    filters: AgentFilter = {};

    @observable
    initialLoading = true;

    @observable
    agentLoading = false;

    constructor(
        @inject(AgentGetAllUseCase)
        private readonly agentGetAllUseCase: AgentGetAllUseCase,
        @inject(CropTypeGetAllUseCase)
        private readonly cropTypeGetAllUseCase: CropTypeGetAllUseCase,
        @inject(PathogenTypeGetAllUseCase)
        private readonly pathogenTypeGetAllUseCase: PathogenTypeGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([this.fetchAgents(), this.fetchCropTypes()]);
        this.loadPathogenTypes();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async fetchAgents(filters?: AgentFilter): Promise<void> {
        if (filters) {
            this.filters = filters;
            this.agentLoading = true;
        }

        const agentResult = await this.agentGetAllUseCase.execute(this.filters);

        runInAction(() => {
            this.agents = agentResult;
            this.agentLoading = false;
        });
    }

    @action
    private async fetchCropTypes(): Promise<void> {
        const cropTypes = await this.cropTypeGetAllUseCase.execute();

        runInAction(() => {
            this.cropTypes = cropTypes;
        });
    }

    @action
    private loadPathogenTypes(): void {
        const pathogenTypes = this.pathogenTypeGetAllUseCase.execute();

        runInAction(() => {
            this.pathogenTypes = pathogenTypes;
        });
    }
}
